import { z } from "zod";
import { Color, Document, Effect, TextEffect } from "./document";

/** Shared HTTP contract between apps/web and apps/server. */

export const DesignSummary = z.object({
  id: z.string(),
  name: z.string(),
  rev: z.int(),
  width: z.number(),
  height: z.number(),
  updatedAt: z.string(),
  hasThumbnail: z.boolean(),
  /** Page count, where the backend tracks it. */
  pages: z.int().optional(),
});
export type DesignSummary = z.infer<typeof DesignSummary>;

export const DesignResponse = z.object({ rev: z.int(), updatedAt: z.string(), document: Document });
export type DesignResponse = z.infer<typeof DesignResponse>;

export const CreateDesignRequest = z.object({ document: Document });
export type CreateDesignRequest = z.infer<typeof CreateDesignRequest>;

/** Optimistic concurrency (SPEC.md §6): the write succeeds only if baseRev is current. */
export const SaveDesignRequest = z.object({ baseRev: z.int().nonnegative(), document: Document });
export type SaveDesignRequest = z.infer<typeof SaveDesignRequest>;

export const SaveDesignResponse = z.object({ rev: z.int(), updatedAt: z.string() });
export type SaveDesignResponse = z.infer<typeof SaveDesignResponse>;

export const ConflictResponse = z.object({ error: z.literal("conflict"), currentRev: z.int() });
export type ConflictResponse = z.infer<typeof ConflictResponse>;

export const AssetUploadResponse = z.object({
  id: z.string(),
  mime: z.string(),
  bytes: z.int(),
  created: z.boolean(),
});
export type AssetUploadResponse = z.infer<typeof AssetUploadResponse>;

export const MeResponse = z.object({ email: z.string(), authMode: z.enum(["access", "header", "dev"]) });
export type MeResponse = z.infer<typeof MeResponse>;

export const ErrorResponse = z.object({ error: z.string(), issues: z.array(z.object({ path: z.string(), message: z.string() })).optional() });
export type ErrorResponse = z.infer<typeof ErrorResponse>;

/** Brand kit lite (SPEC.md §5): saved colours and the fonts text presets use. */
/** A saved look (Text effects → Save to brand styles): element effects plus a text style and curve. */
export const BrandStyle = z.object({
  id: z.string().max(40),
  name: z.string().max(60),
  effects: z.array(Effect).max(16),
  textEffect: TextEffect.optional(),
  curve: z.number().min(-100).max(100).optional(),
});
export type BrandStyle = z.infer<typeof BrandStyle>;

export const BrandKit = z.object({
  colors: z.array(Color).max(64),
  fonts: z.object({ heading: z.string().max(100).nullable(), subheading: z.string().max(100).nullable(), body: z.string().max(100).nullable() }),
  /** Optional so kits saved before styles existed stay valid. */
  styles: z.array(BrandStyle).max(32).optional(),
});
export type BrandKit = z.infer<typeof BrandKit>;
export const EMPTY_BRAND: BrandKit = { colors: [], fonts: { heading: null, subheading: null, body: null } };

/**
 * Version-history retention, shared by the server and the browser-local backend: the last
 * `keep` autosaves, a checkpoint at most every `checkpointMinutes` (kept `checkpointDays`),
 * and named versions forever.
 */
export const REVISIONS = { keep: 20, checkpointMinutes: 10, checkpointDays: 30 } as const;

/** Limits shared by both sides so the client can reject early. */
export const LIMITS = {
  maxDocumentBytes: 5 * 1024 * 1024,
  maxAssetBytes: 50 * 1024 * 1024,
  maxImagePixels: 100_000_000,
  maxThumbnailBytes: 512 * 1024,
} as const;
